import { useEffect, useRef, useState } from 'react';
import { Icon } from '@/shared/components/ui/Icon';
import { useI18n } from '@/shared/i18n/useT';
import { TEMPLATES, TEMPLATE_ORDER } from '@/shared/folders';
import type { FoldersDocType } from '@/shared/folders';

interface Props {
  onCreateFolder: () => void;
  onCreateDocument: (docType: FoldersDocType) => void;
  disabled?: boolean;
  /** Which edge the dropdown lines up with; `left` when the button opens a row. */
  align?: 'left' | 'right';
  /** Round icon-only floating button whose menu opens upward (phones). */
  fab?: boolean;
}

/**
 * The one primary action of the screen.
 *
 * Canva puts creating first and browsing second, which is the right order for a
 * product where the empty state is the common first visit. Templates come from
 * `templates.ts`, so adding "Notes" later is one entry there and nothing here.
 */
export function CreateMenu({ onCreateFolder, onCreateDocument, disabled, align = 'right', fab = false }: Props) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const wrapper = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const onPointer = (e: PointerEvent) => {
      if (!wrapper.current?.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('pointerdown', onPointer);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('pointerdown', onPointer);
    };
  }, [open]);

  return (
    <div ref={wrapper} className="relative">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label={fab ? t('folders.create') : undefined}
        className={
          fab
            ? 'flex h-14 w-14 items-center justify-center rounded-full bg-accent text-accent-fg shadow-lg disabled:opacity-50'
            : 'tap-target flex items-center gap-2 rounded-full bg-accent px-4 text-sm font-medium text-accent-fg disabled:opacity-50'
        }
      >
        <Icon name="plus" className={fab ? 'h-6 w-6' : 'h-4 w-4'} />
        {!fab && t('folders.create')}
      </button>

      {open && (
        <div
          role="menu"
          className={`absolute ${align === 'left' ? 'left-0' : 'right-0'} ${fab ? 'bottom-full mb-2' : 'mt-1'} z-40 w-56 overflow-hidden rounded-2xl border border-border bg-surface shadow-lg`}
        >
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              onCreateFolder();
            }}
            className="tap-target flex w-full items-center gap-3 px-3 text-left text-sm text-fg hover:bg-surface-3"
          >
            <Icon name="folderPlus" className="h-4 w-4 shrink-0" />
            {t('folders.createFolder')}
          </button>

          <div className="border-t border-border" />

          {TEMPLATE_ORDER.map((docType) => {
            const template = TEMPLATES[docType];
            return (
              <button
                key={docType}
                type="button"
                role="menuitem"
                onClick={() => {
                  setOpen(false);
                  onCreateDocument(docType);
                }}
                className="tap-target flex w-full items-center gap-3 px-3 text-left text-sm text-fg hover:bg-surface-3"
              >
                <Icon name={template.icon} className="h-4 w-4 shrink-0" />
                {t(template.nameKey)}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}






